// Preact app bundle entry (esbuild → public/app.js). Loaded AFTER the classic inline
// <script> in index.html via `defer`, so window.app and every render()/action() helper
// are already global by the time this runs. This module is the incremental-migration
// handle: it exposes Preact + an htm `html` tag on window.__preact so migrated screens
// can mount declaratively (Preact diffs instead of clobbering) while the classic
// orchestrator keeps driving every screen not yet migrated. See PREACT-MIGRATION.md.
import { h, render as prender } from 'preact';
import { useRef, useEffect, useMemo, useState } from 'preact/hooks';
import { html } from './shared.js';
import { Home } from './components/Home.js';
import { JoinLanding } from './components/JoinLanding.js';
import { Browse } from './components/Browse.js';
import { Rules } from './components/Rules.js';
import { Standings } from './components/Standings.js';
import { Leaderboard } from './components/Leaderboard.js';
import { Invite } from './components/Invite.js';
import { Scoreboard } from './components/Scoreboard.js';
import { Drawer } from './components/Drawer.js';
import { Controls } from './components/Controls.js';
import { Hand } from './components/Hand.js';
import { Feed } from './components/Feed.js';
import { ConfirmDialog } from './components/ConfirmDialog.js';
import { SeatSheet } from './components/SeatSheet.js';
import { Me } from './components/Me.js';
import { ScriptBoard } from './components/ScriptBoard.js';
import { Arena } from './components/Arena.js';
import { Claim } from './components/Claim.js';

window.__preact = { h, html, prender, useRef, useEffect, useMemo, useState };

// mountInto(container, vnode) — render a vnode into a container that the CLASSIC code path may also
// write to via innerHTML (the connecting-screen early-return, and the e2e string-shim pokes). When
// an external writer replaces the container's DOM, Preact's retained vnode tree no longer matches
// the live DOM, so a plain re-render silently no-ops (stale state). We detect that (the recorded
// first child is gone) and reset with prender(null, container) — the documented Preact unmount —
// before the fresh render. On a NORMAL re-mount nothing is reset, so Preact DIFFS the SAME nodes and
// the focused <textarea> keeps focus / IME / caret (the Stage-4 payoff). See PREACT-MIGRATION.md.
const __pmark = new WeakMap();
const mountInto = (container, vnode) => {
  if (!container) return;
  const marked = __pmark.get(container);
  if (marked && marked !== container.firstChild) prender(null, container); // externally clobbered → hard reset
  prender(vnode, container);
  __pmark.set(container, container.firstChild);
};

// ---- Migrated-screen mount points (one window.__mount<Screen> per screen) ----
// The classic render()/show* path calls these (flag-gated, old static markup kept as the
// fallback shim) instead of building the screen's DOM by hand. Each takes the live container
// element + the global `app` and renders the component fresh — Preact DIFFS, so re-mounts
// reflect state without clobbering inputs (the bug-class fix). See PREACT-MIGRATION.md.
window.__mountHome = (container, app) => prender(html`<${Home} app=${app} />`, container);

// <JoinLanding> (#join invite landing) — classic showJoinLanding()/renderJoinInfo() stash the
// room code + fetched status on app.landingCode / app.landingState, then re-mount; the
// component derives #joinRoomInfo + the join/spectate button show-hide declaratively.
window.__mountJoinLanding = (container, app) => prender(html`<${JoinLanding} app=${app} />`, container);

// <Browse> (#browse public lobby list) — classic showBrowse()/fetchBrowse()/renderBrowseList()
// stash the fetched rooms on app.browseRooms (null while loading) and re-mount; the component
// renders the head/count/list/foot scaffold + one <RoomCard> per room.
window.__mountBrowse = (container, app) => prender(html`<${Browse} app=${app} />`, container);

// ---- Stage 3: overlay/modal surfaces, each mounted by its EXISTING show*/render* window fn ----
// Each classic show* fn stashes its dynamic data on `app` (cast/leaderboard/inviteUrl/scoreboard)
// then mounts the matching component into its overlay container. Components are PURE functions of
// `app` and emit the SAME class names / data-action attrs / text, so document-level click
// delegation + the smoke grep contract stay green. The old string-builders are kept as fallback
// shims behind the window.__USE_PREACT_OVERLAYS flag for one-line rollback. See PREACT-MIGRATION.md.

// <Rules> (#rules) — static rules text, mounted by showRules().
window.__mountRules = (container, app) => prender(html`<${Rules} app=${app} />`, container);
// <Standings> (#standings) — cumulative room ranking, mounted anytime by showStandings().
window.__mountStandings = (container, app) => prender(html`<${Standings} app=${app} />`, container);
// <Leaderboard> (#leaderboard) — showLeaderboard() fetches /api/leaderboard, stashes app.leaderboard, mounts.
window.__mountLeaderboard = (container, app) => prender(html`<${Leaderboard} app=${app} />`, container);
// <Invite> (#invite) — showInvite() stashes app.inviteUrl, then mounts the share sheet (+ the agent invite).
window.__mountInvite = (container, app) => prender(html`<${Invite} app=${app} />`, container);
// <Scoreboard> (#scoreboard) — renderScoreboard() guards the once-per-game sig, then mounts the
// result card; paintScoreRows() re-mounts it so live 小红花 totals diff (no pop replay). The
// nested <ScoreRows> lives inside this one Preact root (no nested-root fight).
window.__mountScoreboard = (container, app) => prender(html`<${Scoreboard} app=${app} />`, container);
// <Drawer> (#log inside .drawer) — renderLog() mounts the room-history lines into the secondary drawer.
window.__mountDrawer = (container, app) => prender(html`<${Drawer} app=${app} />`, container);
// <ConfirmDialog> (#confirm) — our styled confirm modal, mounted by showConfirm() in place of native confirm().
window.__mountConfirm = (container, app) => prender(html`<${ConfirmDialog} app=${app} />`, container);
window.__mountSeatSheet = (container, app) => prender(html`<${SeatSheet} app=${app} />`, container);
// <Me> (#me) — the unified identity sheet (face + name + account), mounted by showMe().
// Replaces the old separate <FaceBuilder> popup + string-built account overlay.
window.__mountMe = (container, app) => prender(html`<${Me} app=${app} />`, container);

// ---- Stage 4: #controls + #hand + #feed (THE payoff) ----
// The classic render()/paintFeed() path mounts these (flag-gated) instead of innerHTML = helperString().
// <Controls> uses CONTROLLED speech/chat boxes (value=${app.speechDraft}/${app.chatDraft} + onInput
// re-mount) so focus / IME / caret survive a re-render WITHOUT preserveTyping — the bug-class fix.
// Because Preact DIFFS the SAME <textarea> node, the focused input is never destroyed on a poll/emoji
// re-mount. The classic renderControls/renderFeed/renderHand string-builders are kept as fallback shims
// (and e2e-interactions still assigns renderControls()'s STRING return to innerHTML). See PREACT-MIGRATION.md.
window.__mountControls = (container, app) => mountInto(container, html`<${Controls} app=${app} />`);
window.__mountHand = (container, app) => mountInto(container, html`<${Hand} app=${app} />`);
window.__mountFeed = (container, app) => mountInto(container, html`<${Feed} app=${app} />`);

// ---- Agent arena (design/AGENT-ARENA.md): spectator-first script board, home arena board, claim page ----
window.__mountScript = (container, app) => mountInto(container, html`<${ScriptBoard} app=${app} />`);
window.__mountArena = (container, app) => mountInto(container, html`<${Arena} app=${app} />`);
window.__mountClaim = (container, app) => mountInto(container, html`<${Claim} app=${app} />`);

window.__preactReady = true;
// Optional ready hook so the classic script (or a test) can wait for the bundle.
if (typeof window.__onPreactReady === 'function') window.__onPreactReady();
